﻿import { ref, markRaw, Component } from 'vue';
import type { ProviderLicenseEditorProps } from '@/modals/ProviderLicenseEditor.vue';
import type { ConfirmationProps } from '@/modals/Confirmation.vue';
import type { LocationEditorProps } from '@/modals/LocationEditor.vue';

export interface ModalProps {
    ProviderLicenseEditor: ProviderLicenseEditorProps,
    Confirmation: ConfirmationProps,
    LocationEditor: LocationEditorProps,
}

type ModalEntry = {
    component: Component,
    props: Record<string, any>
    resolve?: (value: boolean | Object) => void
}

type ModalModule = { default: Component };
type ModalName = keyof ModalProps;

const stack = ref<ModalEntry[]>([]);
const modalLoaders: Record<ModalName, () => Promise<ModalModule>> = {
    Confirmation: () => import('@/modals/Confirmation.vue'),
    LocationEditor: () => import('@/modals/LocationEditor.vue'),
    ProviderLicenseEditor: () => import('@/modals/ProviderLicenseEditor.vue'),
};
const loadedModals: Partial<Record<ModalName, Promise<ModalModule>>> = {};

const loadModal = (name: ModalName) => {
    loadedModals[name] ??= modalLoaders[name]();
    return loadedModals[name]!;
}

export const preloadModals = () => {
    Object.keys(modalLoaders).forEach((name) => {
        void loadModal(name as ModalName);
    });
}

export const openModal = async <K extends keyof ModalProps>(
    name: K,
    props: ModalProps[K]
): Promise<boolean | undefined | Object> => {
    const module = await loadModal(name);
    return new Promise((resolve) => {
        stack.value.push(markRaw({ component: module.default, props, resolve }))
    })
}

const closeModal = (result?: boolean | Object) => {
    const modal = stack.value.pop();

    if (modal?.resolve) {
        modal.resolve(result);
    }
};

const closeAllModals = (result?: boolean | Object) => {
    const modals = stack.value;
    modals.forEach(modal => modal.resolve?.(false));
    stack.value = [];
}

export function useModal() {
    return {
        stack,
        openModal,
        closeModal,
        closeAllModals,
    };
}
